<script lang="ts">
  import { FONT_STACK } from '../ink/text';
  import { INK_TOKENS, resolveInk } from '../theme/tokens';
  import { app } from './appState.svelte';
  import type { InkEngine } from '../ink/engine';
  import type { TextBox } from '../store/types';

  let { engine, box, onclose }: { engine: InkEngine; box: TextBox; onclose: () => void } = $props();

  /** fontSize presets (world px), spec §1.1. */
  const SIZES = [12, 15, 20, 28];
  const LINE_HEIGHT = 1.4; // must match ink/text.ts

  // Working copy of the editable fields. `box` is a clone the Editor made —
  // never mutate it; save() hands the engine a fresh record (repo restamps
  // updatedAt).
  let text = $state(box.text);
  let fontSize = $state(box.fontSize);
  let bold = $state(box.bold);
  let italic = $state(box.italic);
  let color = $state(box.color);

  let vp = $state(engine.getViewport());
  let ta = $state<HTMLTextAreaElement | null>(null);
  let container = $state<HTMLDivElement | null>(null);
  let finished = false;

  // Track pan/zoom so the overlay stays glued to the box's world rect. The
  // engine has no off(); the guard keeps a stale listener inert after close
  // (same accepted pattern as SelectionBar).
  $effect(() => {
    let alive = true;
    engine.on('viewport', (v) => { if (alive) vp = v; });
    return () => { alive = false; };
  });

  // Screen-space rect of the box under the current viewport.
  let left = $derived((box.x - vp.x) * vp.scale);
  let top = $derived((box.y - vp.y) * vp.scale);
  let width = $derived(box.w * vp.scale);
  let fontPx = $derived(fontSize * vp.scale);
  // The style strip sits above the box unless that would push it off-screen.
  let stripBelow = $derived(top < 56);

  // Auto-grow: collapse then match content height whenever text/metrics change.
  $effect(() => {
    void text; void fontPx; void bold; void italic; void width;
    const el = ta;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.max(el.scrollHeight, Math.ceil(fontPx * LINE_HEIGHT))}px`;
  });

  $effect(() => { ta?.focus(); });

  /**
   * Persist the working copy — exported so the Editor can force a save before
   * page navigation / tool switches. Empty text = delete (spec §1.3);
   * unchanged = no-op (no phantom undo entries or sync traffic). Idempotent.
   */
  export async function save(): Promise<void> {
    if (finished) return;
    finished = true;
    if (text.trim() === '') { await engine.deleteTextBox(box.id); return; }
    if (text === box.text && fontSize === box.fontSize && bold === box.bold && italic === box.italic && color === box.color) return;
    await engine.updateTextBox({ ...box, text, fontSize, bold, italic, color });
  }

  async function done() {
    await save();
    onclose();
  }

  async function del() {
    if (finished) return;
    finished = true;
    await engine.deleteTextBox(box.id);
    onclose();
  }

  /** Blur out of the whole editor (e.g. a canvas tap) saves — strip clicks keep focus inside. */
  function onBlur(e: FocusEvent) {
    const next = e.relatedTarget as Node | null;
    if (next && container?.contains(next)) return;
    void done();
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); void done(); }
  }

  // Unexpected unmount (remote purge routed us away, etc.): best-effort save.
  $effect(() => () => { if (!finished) void save(); });
</script>

<div class="text-editor" bind:this={container} style="left:{left}px; top:{top}px; width:{Math.max(width, 40)}px">
  <div class="strip" class:below={stripBelow}>
    {#each SIZES as s (s)}
      <button class:active={fontSize === s} onclick={() => (fontSize = s)} title="Size {s}">{s}</button>
    {/each}
    <button class="fmt bold" class:active={bold} onclick={() => (bold = !bold)} title="Bold">B</button>
    <button class="fmt italic" class:active={italic} onclick={() => (italic = !italic)} title="Italic">I</button>
    {#each INK_TOKENS as t (t)}
      <button class="dot" class:active={color === t} style="background:{resolveInk(t, app.theme)}" onclick={() => (color = t)} aria-label={t} title={t}></button>
    {/each}
    <button onclick={del} title="Delete text box">🗑</button>
    <button class="done" onclick={done} title="Done">Done</button>
  </div>
  <textarea
    bind:this={ta}
    bind:value={text}
    onblur={onBlur}
    onkeydown={onKeydown}
    spellcheck="false"
    aria-label="Text box"
    style="font-family:{FONT_STACK}; font-size:{fontPx}px; line-height:{LINE_HEIGHT}; font-weight:{bold ? 700 : 400}; font-style:{italic ? 'italic' : 'normal'}; color:{resolveInk(color, app.theme)}"
  ></textarea>
</div>

<style>
  .text-editor { position: absolute; z-index: 6; }
  textarea {
    display: block; width: 100%; margin: 0; padding: 0; border: none; resize: none;
    overflow: hidden; background: var(--paper); outline: 1.5px dashed var(--accent);
    outline-offset: 2px; border-radius: 2px; overflow-wrap: break-word; word-break: break-word;
  }
  .strip {
    position: absolute; bottom: 100%; left: 0; margin-bottom: 10px;
    display: flex; align-items: center; gap: 3px; flex-wrap: wrap; max-width: max(100%, 340px);
    background: var(--surface); border-radius: var(--radius-panel); box-shadow: var(--shadow-soft); padding: 4px;
  }
  .strip.below { bottom: auto; top: 100%; margin-bottom: 0; margin-top: 10px; }
  .strip button {
    min-width: 32px; height: 32px; border: none; border-radius: 8px; padding: 0 6px;
    background: transparent; color: var(--ink-primary); font-size: 13px; cursor: pointer;
  }
  .strip button.active { background: var(--accent); color: #fff; }
  .fmt.bold { font-weight: 700; }
  .fmt.italic { font-style: italic; }
  .dot { min-width: 22px !important; width: 22px; height: 22px !important; border-radius: 50% !important; border: 2px solid transparent !important; padding: 0 !important; }
  .dot.active { border-color: var(--accent) !important; background-clip: padding-box; }
  .done { font-weight: 700; color: var(--accent) !important; }
  .done.active, .done:hover { color: var(--accent); }
</style>
